import React, { useCallback, useContext, useMemo } from 'react';
import dayjs from 'dayjs';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import { t } from 'i18next';
import { TxStep } from '@sovryn/sdk';
import { Button, ErrorBadge, ErrorLevel } from '@sovryn/ui';
import { RSK_CHAIN_ID } from '../../../../../../config/chains';
import { TransactionIdRenderer } from '../../../../../2_molecules/TransactionIdRenderer/TransactionIdRenderer';
import { TxIdWithNotification } from '../../../../../2_molecules/TxIdWithNotification/TransactionIdWithNotification';
import { getTokenDisplayName } from '../../../../../../constants/tokens';
import { useAccount } from '../../../../../../hooks';
import { useTokenDetailsByAsset } from '../../../../../../hooks/useTokenDetailsByAsset';
import { translations } from '../../../../../../locales/i18n';
import { sharedState } from '../../../../../../store/rxjs/shared-state';
import { getChainById } from '../../../../../../utils/chain';
import { formatValue } from '../../../../../../utils/math';
import { SendFlowContext, SendFlowStep } from '../../../contexts/sendflow';
import { useBridge } from '../../../hooks/useBridge';
import { useBridgeLimits } from '../../../hooks/useBridgeLimits';
import { useBridgeService } from '../../../hooks/useBridgeService';
import { useERC20BridgeLocked } from '../../../hooks/useERC20BridgeLocked';
import { TxStatusTitle } from '../../TxStatusTitle';
const translation = translations.erc20Bridge.confirmationScreens;
export const ReviewScreen = () => {
    const { token, chainId, amount, receiver, set } = useContext(SendFlowContext);
    const bridgeService = useBridgeService();
    const { data: limits } = useBridgeLimits(RSK_CHAIN_ID, chainId, token);
    const { account } = useAccount();
    const sourceChain = getChainById(RSK_CHAIN_ID);
    const targetChain = getChainById(chainId);
    const assetDetails = useTokenDetailsByAsset(token, RSK_CHAIN_ID);
    const { handleSubmit, transaction } = useBridge({
        sourceChain: RSK_CHAIN_ID,
        targetChain: chainId,
        asset: token,
        amount: parseUnits(amount || '0', assetDetails === null || assetDetails === void 0 ? void 0 : assetDetails.decimals).toString(),
        receiver,
        onSuccess: () => set(prev => (Object.assign(Object.assign({}, prev), { step: SendFlowStep.COMPLETED }))),
        onTxStart: () => set(prev => (Object.assign(Object.assign({}, prev), { step: SendFlowStep.PROCESSING }))),
    });
    const handleErc20BridgeDialogClose = useCallback(() => sharedState.actions.closeErc20BridgeDialog(), []);
    const items = useMemo(() => {
        var _a, _b;
        if (transaction.step === TxStep.IDLE) {
            return [
                {
                    label: t(translation.amount),
                    value: React.createElement(React.Fragment, null, formatValue(Number(amount), 8)),
                },
                {
                    label: t(translation.token),
                    value: (React.createElement(React.Fragment, null,
                        token,
                        " ",
                        '->',
                        " ",
                        token)),
                },
                {
                    label: t(translation.originNetwork),
                    value: (_a = bridgeService.getNetworkConfig(RSK_CHAIN_ID)) === null || _a === void 0 ? void 0 : _a.name,
                },
                {
                    label: t(translation.destinationNetwork),
                    value: chainId ? (_b = bridgeService.getNetworkConfig(chainId)) === null || _b === void 0 ? void 0 : _b.name : '',
                },
                {
                    label: t(translation.date),
                    value: dayjs().format('M/D/YYYY'),
                },
                {
                    label: t(translation.serviceFee),
                    value: limits && token
                        ? `${formatUnits(limits.feePerToken)} ${getTokenDisplayName(token)}`
                        : '-',
                },
                {
                    label: t(translation.receiver),
                    value: (React.createElement(TxIdWithNotification, { href: `${targetChain === null || targetChain === void 0 ? void 0 : targetChain.blockExplorerUrl}/address/${receiver}`, value: receiver })),
                },
            ];
        }
        const items = [
            {
                label: t(translation.from),
                value: (React.createElement(TxIdWithNotification, { href: `${sourceChain === null || sourceChain === void 0 ? void 0 : sourceChain.blockExplorerUrl}/address/${account}`, value: account })),
            },
            {
                label: t(translation.to),
                value: (React.createElement(TxIdWithNotification, { href: `${targetChain === null || targetChain === void 0 ? void 0 : targetChain.blockExplorerUrl}/address/${receiver}`, value: receiver })),
            },
            {
                label: t(translation.serviceFee),
                value: limits && token
                    ? `${formatUnits(limits.feePerToken)} ${getTokenDisplayName(token)}`
                    : '-',
            },
            {
                label: t(translation.sendingAmount),
                value: (React.createElement(React.Fragment, null,
                    formatValue(Number(amount), 8),
                    " ",
                    getTokenDisplayName(token))),
            },
            {
                label: t(translation.receivingAmount),
                value: (React.createElement(React.Fragment, null,
                    formatValue(Number(amount) - Number(formatUnits((limits === null || limits === void 0 ? void 0 : limits.feePerToken) || '0')), 8),
                    ' ',
                    getTokenDisplayName(token))),
            },
        ];
        if (transaction.transferHash) {
            items.push({
                label: t(translation.rootstockTxId),
                value: (React.createElement(TransactionIdRenderer, { hash: transaction.transferHash, chainId: RSK_CHAIN_ID })),
            });
        }
        return items;
    }, [
        account,
        amount,
        bridgeService,
        chainId,
        limits,
        receiver,
        sourceChain === null || sourceChain === void 0 ? void 0 : sourceChain.blockExplorerUrl,
        targetChain === null || targetChain === void 0 ? void 0 : targetChain.blockExplorerUrl,
        token,
        transaction.step,
        transaction.transferHash,
    ]);
    const isLoading = [
        TxStep.APPROVING,
        TxStep.CONFIRMING,
        TxStep.PENDING,
    ].includes(transaction.step);
    const isBridgeLocked = useERC20BridgeLocked();
    return (React.createElement("div", { className: "text-center" },
        React.createElement(TxStatusTitle, { step: transaction.step }),
        React.createElement("div", { className: "bg-gray-80 border rounded border-gray-50 p-3 text-xs text-gray-30 flex flex-col gap-3" }, items.map(({ label, value }, index) => (React.createElement("div", { className: "flex justify-between", key: label },
            React.createElement("span", null,
                label,
                " "),
            React.createElement("span", null, value))))),
        isBridgeLocked ? (React.createElement(ErrorBadge, { level: ErrorLevel.Warning, message: t(translations.maintenanceMode.erc20Bridge) })) : (React.createElement("div", { className: "mt-12" },
            React.createElement(Button, { text: transaction.step === TxStep.CONFIRMED
                    ? t(translations.common.buttons.done)
                    : [TxStep.USER_DENIED, TxStep.FAILED].includes(transaction.step)
                        ? t(translations.common.buttons.retry)
                        : t(translations.common.buttons.confirm), onClick: transaction.step === TxStep.CONFIRMED
                    ? handleErc20BridgeDialogClose
                    : handleSubmit, loading: isLoading, disabled: isLoading || isBridgeLocked, className: "w-full", dataAttribute: "erc20-send-confirm" })))));
};
